import { Badge } from '../../components/ui/Badge';
import { isHexColour, tokenContrast } from '../../lib/email-design';
import type { EmailDesign, EmailFont, EmailIdentity, EmailTokens } from '../../queries/brandEmailTheme';
import { StyleFields, WelcomeFields } from './EmailStyleFields';

const inputClass = 'mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm';

type ColourField = { key: keyof EmailTokens; label: string; hint: string };

const COLOUR_GROUPS: { title: string; fields: ColourField[] }[] = [
  {
    title: 'Background',
    fields: [
      { key: 'page', label: 'Page', hint: 'The background around the email card.' },
      { key: 'card', label: 'Card', hint: 'The email itself. Text contrast is measured against this.' },
      { key: 'border', label: 'Border', hint: 'Card border, the line under the letterhead, dividers and stat tiles.' },
      { key: 'rule', label: 'Rule', hint: 'The thin lines between rows in lists and receipts.' },
      { key: 'panel', label: 'Panel', hint: 'Boxes inside the card: codes, notices, quotes.' },
      { key: 'panelBorder', label: 'Panel border', hint: 'The edge of those boxes and outline pills.' },
    ],
  },
  {
    title: 'Text',
    fields: [
      { key: 'heading', label: 'Heading', hint: 'Headings, key receipt values and the big code digits.' },
      { key: 'strong', label: 'Strong', hint: 'The greeting, bold words, the sign-off name and quotes.' },
      { key: 'body', label: 'Body', hint: 'Ordinary paragraphs.' },
      { key: 'label', label: 'Label', hint: 'Small capital labels, such as receipt keys and the tagline.' },
      { key: 'muted', label: 'Muted', hint: 'Notes, meta lines and footer links.' },
      { key: 'faint', label: 'Faint', hint: 'Legal small print in the footer only (needs 3:1).' },
    ],
  },
  {
    title: 'Accent',
    fields: [
      { key: 'accent', label: 'Accent', hint: 'Eyebrows, the accent headline line, ticks, links and panel edges.' },
      { key: 'button', label: 'Button', hint: 'The fill of the main button.' },
      { key: 'buttonText', label: 'Button text', hint: 'The words on the main button.' },
      { key: 'onAccent', label: 'On accent', hint: 'Text on a pill filled with the accent colour.' },
      { key: 'highlight', label: 'Highlight', hint: 'Expiry dates, deadlines and urgency only.' },
    ],
  },
];

type IdentityField = { key: keyof EmailIdentity; label: string; hint: string; type?: 'url' | 'number' };

const IDENTITY_FIELDS: IdentityField[] = [
  { key: 'wordmark', label: 'Wordmark', hint: 'The brand name as text in the letterhead. Leave blank if the logo carries the name.' },
  { key: 'wordmarkAccent', label: 'Wordmark accent', hint: 'The end of the wordmark shown in the accent colour, e.g. "IQ". Leave blank for one colour.' },
  { key: 'tagline', label: 'Tagline', hint: 'The small line under the wordmark.' },
  { key: 'logoUrl', label: 'Logo URL', hint: 'An https:// image hosted on the site. Leave blank for the wordmark alone.', type: 'url' },
  { key: 'logoWidth', label: 'Logo width (px)', hint: '8 to 200.', type: 'number' },
  { key: 'logoHeight', label: 'Logo height (px)', hint: '8 to 200.', type: 'number' },
  { key: 'footerTaglineLead', label: 'Footer tagline (first part)', hint: 'Shown in the body colour.' },
  { key: 'footerTaglineAccent', label: 'Footer tagline (second part)', hint: 'Shown in the accent colour.' },
  { key: 'signOffName', label: 'Sign-off name', hint: 'Who the email is from, e.g. "The Racing Desk".' },
];

function ContrastChip({ tokens, tokenKey }: { tokens: EmailTokens; tokenKey: keyof EmailTokens }) {
  const result = tokenContrast(tokens, tokenKey);
  if (!result) return null;
  const ratio = `${result.ratio.toFixed(2)}:1`;
  return (
    <Badge
      variant={result.pass ? 'success' : 'destructive'}
      title={`Contrast against ${result.against}: ${ratio}; needs ${result.min}:1`}
      data-testid={`contrast-${tokenKey}`}
    >
      {result.pass ? 'AA pass' : 'AA fail'} {ratio}
    </Badge>
  );
}

interface EmailDesignFormProps {
  design: EmailDesign;
  fonts: EmailFont[];
  canWrite: boolean;
  onChange: (design: EmailDesign) => void;
}

/** The colour tokens, style, letterhead identity and welcome copy of a brand's email design. */
export function EmailDesignForm({ design, fonts, canWrite, onChange }: EmailDesignFormProps) {
  const setToken = (key: keyof EmailTokens, value: string) => onChange({ ...design, tokens: { ...design.tokens, [key]: value } });
  const setIdentity = (field: IdentityField, raw: string) => {
    let value: string | number | null | undefined = raw;
    if (field.type === 'number') value = raw === '' ? undefined : Number(raw);
    else if (field.key === 'logoUrl') value = raw.trim() === '' ? null : raw;
    onChange({ ...design, identity: { ...design.identity, [field.key]: value } });
  };

  return (
    <div className="space-y-6">
      <StyleFields style={design.style} fonts={fonts} canWrite={canWrite} onChange={(style) => onChange({ ...design, style })} />
      {COLOUR_GROUPS.map((group) => (
        <fieldset key={group.title} className="space-y-3">
          <legend className="mb-2 text-sm font-medium">{group.title}</legend>
          {group.fields.map((field) => {
            const value = design.tokens[field.key] ?? '';
            return (
              <div key={field.key} className="text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="color"
                    aria-label={`${field.label} colour picker`}
                    className="h-8 w-10 shrink-0 cursor-pointer rounded border border-border bg-transparent p-0.5 disabled:cursor-default"
                    value={isHexColour(value) ? value.toLowerCase() : '#000000'}
                    disabled={!canWrite}
                    onChange={(e) => setToken(field.key, e.target.value)}
                  />
                  <label className="flex items-center gap-2">
                    <span className="w-24 text-muted-foreground">{field.label}</span>
                    <input
                      className="w-28 rounded-md border border-input bg-background px-2 py-1 font-mono text-xs"
                      aria-label={`${field.label} colour`}
                      aria-invalid={!isHexColour(value)}
                      value={value}
                      maxLength={7}
                      spellCheck={false}
                      disabled={!canWrite}
                      onChange={(e) => setToken(field.key, e.target.value.trim())}
                    />
                  </label>
                  <ContrastChip tokens={design.tokens} tokenKey={field.key} />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{field.hint}</p>
              </div>
            );
          })}
        </fieldset>
      ))}

      <fieldset className="space-y-3">
        <legend className="mb-2 text-sm font-medium">Letterhead and sign-off</legend>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {IDENTITY_FIELDS.map((field) => {
            const value = design.identity[field.key];
            return (
              <label key={field.key} className="block text-sm">
                <span className="text-muted-foreground">{field.label}</span>
                <input
                  className={inputClass}
                  type={field.type ?? 'text'}
                  min={field.type === 'number' ? 8 : undefined}
                  max={field.type === 'number' ? 200 : undefined}
                  value={value == null ? '' : String(value)}
                  disabled={!canWrite}
                  onChange={(e) => setIdentity(field, e.target.value)}
                />
                <span className="mt-1 block text-xs text-muted-foreground">{field.hint}</span>
              </label>
            );
          })}
        </div>
      </fieldset>

      <WelcomeFields identity={design.identity} canWrite={canWrite} onChange={(identity) => onChange({ ...design, identity })} />
    </div>
  );
}
